import type { Auth, ActionCodeSettings } from 'firebase/auth';
import { sendSignInLinkToEmail } from 'firebase/auth';

function normalizeBaseUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed.replace(/\/+$/, '');
  }
  return `https://${trimmed.replace(/\/+$/, '')}`;
}

function buildInviteContinueUrls(inviteId: string): string[] {
  const routes = `/auth/complete-invite?inviteId=${inviteId}`;
  const configuredBaseUrl = normalizeBaseUrl(import.meta.env.VITE_INVITE_CONTINUE_URL || '');
  const runtimeOrigin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const authDomainBaseUrl = normalizeBaseUrl(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '');

  const candidates = [
    configuredBaseUrl ? `${configuredBaseUrl}${routes}` : '',
    runtimeOrigin ? `${runtimeOrigin}${routes}` : '',
    authDomainBaseUrl ? `${authDomainBaseUrl}${routes}` : '',
  ];

  return Array.from(new Set(candidates.filter(Boolean)));
}

function isUnauthorizedContinueUriError(error: unknown): boolean {
  const text = error instanceof Error ? error.message : String(error);
  return (
    text.includes('auth/unauthorized-continue-uri') ||
    text.includes('auth/unauthorized-continue-url')
  );
}

export async function sendInviteSignInLink(auth: Auth, email: string, inviteId: string): Promise<void> {
  const urls = buildInviteContinueUrls(inviteId);
  if (!urls.length) {
    throw new Error(
      'No se pudo generar la URL de invitación. Configura VITE_INVITE_CONTINUE_URL o VITE_FIREBASE_AUTH_DOMAIN.'
    );
  }

  let lastError: unknown;
  for (let i = 0; i < urls.length; i++) {
    const settings: ActionCodeSettings = {
      url: urls[i],
      handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(auth, email, settings);
      return;
    } catch (err) {
      lastError = err;
      const hasMoreFallbacks = i < urls.length - 1;
      if (isUnauthorizedContinueUriError(err) && hasMoreFallbacks) continue;
      throw err;
    }
  }

  throw lastError ?? new Error('No fue posible enviar la invitación.');
}
